import { useEffect, useMemo, useRef, useState } from "react";
import { useProgressStore } from "@/application/stores/progressStore";
import { useSkillMemoryStore } from "@/application/stores/skillMemoryStore";
import { useManifest } from "@/application/usecases/useManifest";
import { usePreloadLevel } from "@/application/usecases/usePreloadLevel";
import { computeHubCta } from "@/domain/learning/hub-cta";
import { dueSkillKeys } from "@/domain/learning/skill-memory";
import type { CompanionExpression } from "@/infrastructure/assets/paths";
import { backgroundAssetPath, companionAssetPath } from "@/infrastructure/assets/paths";
import { Icon } from "@/presentation/components/icons/Icon";
import type { IconName } from "@/presentation/components/icons/icon-names";
import { HeaderZone, MainZone } from "@/presentation/components/layout/Zones";
import { useOverlay } from "@/presentation/components/overlay/OverlayProvider";
import { useBgm } from "@/presentation/features/bgm/useBgm";
import { SettingsPanel } from "@/presentation/features/settings/SettingsPanel";
import { LABELS, levelDisplayName } from "@/presentation/i18n/labels";
import type { Route } from "@/presentation/router/route";
import "./TitleMenu.css";

interface TitleMenuProps {
  readonly navigate: (route: Route) => void;
  /** キー一覧（O-5）を開く。`?` と同じモーダルを品書きからも開けるようにするため。 */
  readonly onOpenKeyList: () => void;
  /**
   * 携帯 / 1024px未満での表示（O-3。ui-design.md §7.8・§4.1）。
   * 稽古への導線（主の押し・段をえらぶ）を出さない（F-010）。既定はfalse。
   */
  readonly isMobileOrNarrow?: boolean;
}

/** 品書きの一項目。門から行ける先はここに列挙されたものがすべて。 */
interface MenuItem {
  readonly key: string;
  readonly label: string;
  /** 項目の下に添える一行。何が起きるかを言い切る（ui-design.md §10）。 */
  readonly note: string;
  readonly icon: IconName;
  readonly primary?: boolean;
  /** 焦点・hoverで先読みを始める段（T-02）。行き先が段でない項目は持たない。 */
  readonly preloadLevelId?: string | undefined;
  readonly onSelect: () => void;
}

/** 進捗の状態ごとの、主の押しの文言と師範代の構え。 */
function heroLabel(cta: ReturnType<typeof computeHubCta>): {
  text: string;
  note: string;
  expression: CompanionExpression;
  speech: string;
} {
  if (cta.kind === "continue") {
    const name = levelDisplayName(cta.levelId) ?? cta.levelId;
    return {
      text: `つづきから（${name}）`,
      note: "中断した段の続きから稽古を始める",
      expression: "neutral",
      speech: `待っていたぞ。${name}の途中だったな。`,
    };
  }
  if (cta.kind === "review") {
    const name = levelDisplayName(cta.levelId) ?? cta.levelId;
    return {
      text: `稽古を見なおす（${name}）`,
      note: "修めた段をもう一度打ち直す",
      expression: "cheer",
      speech: "免許皆伝か。だが腕は使わねば鈍る。好きな段を選べ。",
    };
  }
  return {
    text: "稽古をはじめる",
    note: "一段 運足から順に昇る",
    expression: "smile",
    speech: "よく来たな。ここが vim道場だ。まずは足の運びから教える。",
  };
}

/**
 * S-0 門（`/`）。道場の表に立つタイトルの画面（D-119）。
 *
 * 稽古の段（S-1）が持っていた「次の一手へ送り出す」役目のうち、**行き先を選ぶ**部分だけを
 * 手前に切り出した。段の一覧そのものは S-1 に残っているため、ハブが二つに割れることはない
 * （N-001 は保つ）。ここから辿れるのは、稽古・段の一覧・免状帳・キー一覧・帳（設定）の5つ。
 *
 * 師範代ノーマの立ち絵を主役に据えるのは、話（V-1/V-5）と稽古（V-2/V-3）にしか出ていな
 * かった道場の人を、入口で先に引き合わせるため。立ち絵は素材が右向きなので、左に置いた
 * 品書きへ向くよう水平反転する（AdvScene・Companion と同じ理由・同じ扱い）。
 */
export function TitleMenu({ navigate, onOpenKeyList, isMobileOrNarrow = false }: TitleMenuProps) {
  const { status, levels } = useManifest();
  const progress = useProgressStore((state) => state.progress);
  const skillMemory = useSkillMemoryStore((state) => state.memory);
  const { openPopover } = useOverlay();
  const preload = usePreloadLevel();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useBgm("gate");

  const cta = useMemo(() => computeHubCta(levels ?? [], progress), [levels, progress]);
  const dueCount = useMemo(() => dueSkillKeys(skillMemory).length, [skillMemory]);
  const hero = heroLabel(cta);
  const speech =
    dueCount > 0 ? `${dueCount}つの技が復習どきだ。指が覚える前に、もう一度打て。` : hero.speech;

  /** 主の押しが開く段。未着手なら一段（manifest の先頭）。 */
  const heroLevelId = cta.kind === "start" ? levels?.[0]?.id : cta.levelId;

  const items = useMemo<readonly MenuItem[]>(() => {
    const entries: MenuItem[] = [];
    if (!isMobileOrNarrow) {
      entries.push({
        key: "start",
        label: hero.text,
        note: hero.note,
        icon: "flag",
        primary: true,
        preloadLevelId: heroLevelId,
        onSelect: () => {
          if (heroLevelId) {
            navigate({ name: "level", levelId: heroLevelId });
          }
        },
      });
      if (dueCount > 0) {
        entries.push({
          key: "review",
          label: `復習する（${dueCount}技）`,
          note: "間隔を空けて指に残した技を想起する",
          icon: "target",
          onSelect: () => navigate({ name: "review" }),
        });
      }
      entries.push({
        key: "levels",
        label: "段をえらぶ",
        note: "一段から七段までの品書きを開く",
        icon: "scroll",
        onSelect: () => navigate({ name: "level-select" }),
      });
    }
    entries.push({
      key: "stamps",
      label: LABELS.stampBook,
      note: "授かった朱印をあらためる",
      icon: "mokuroku",
      onSelect: () => navigate({ name: "stamps" }),
    });
    entries.push({
      key: "keys",
      label: "キー一覧",
      note: "画面ごとの打鍵を確かめる",
      icon: "key",
      onSelect: onOpenKeyList,
    });
    return entries;
  }, [dueCount, hero.note, hero.text, heroLevelId, isMobileOrNarrow, navigate, onOpenKeyList]);

  // 門に入った時点で主の押しに焦点を置く（1打で稽古へ入れる。S-1 と同じ作法）。
  // 品書きの生成は manifest 待ちのため、焦点だけ先に置いても preloadLevelId が
  // まだ付いていないことがある。heroLevelId が揃った時点で先読みを必ず発火する
  // （焦点の onFocus にだけ頼ると、manifest 到着後に先読みが一度も走らない）。
  useEffect(() => {
    itemRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (heroLevelId) {
      preload(heroLevelId);
    }
  }, [heroLevelId, preload]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(index + 1, items.length - 1);
      setFocusedIndex(next);
      itemRefs.current[next]?.focus();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prev = Math.max(index - 1, 0);
      setFocusedIndex(prev);
      itemRefs.current[prev]?.focus();
    }
  }

  return (
    <div className="title-menu">
      {/* 門の情景。意味は文字が担うため読み上げからは隠す（ui-design.md §11.2）。 */}
      <img
        className="title-menu__background"
        src={backgroundAssetPath("gate")}
        width={1920}
        height={1080}
        alt=""
        aria-hidden="true"
      />
      <div className="title-menu__scrim" aria-hidden="true" />

      <HeaderZone className="title-menu__header">
        <span className="z-header__label">門</span>
        <span className="z-header__actions">
          <button
            type="button"
            className="z-header__icon-btn"
            aria-label="設定"
            onClick={openPopover}
          >
            <Icon name="gear" title="設定" />
          </button>
        </span>
        <SettingsPanel />
      </HeaderZone>

      <MainZone className="title-menu__main">
        <div className="title-menu__layout">
          <div className="title-menu__brand">
            <Icon name="enso" decorative size={132} className="title-menu__logo" />
            <p className="title-menu__kicker">現代に残る古い道場</p>
            <h1 className="title-menu__title">vim道場</h1>
            <p className="title-menu__lede">七段を昇り、打鍵だけで紙面を操る技を修める。</p>

            <nav className="title-menu__nav" aria-label="品書き">
              <ul className="title-menu__list">
                {items.map((item, index) => (
                  <li key={item.key}>
                    <button
                      ref={(el) => {
                        itemRefs.current[index] = el;
                      }}
                      type="button"
                      className={
                        item.primary
                          ? "title-menu__item title-menu__item--primary"
                          : "title-menu__item"
                      }
                      tabIndex={index === focusedIndex ? 0 : -1}
                      onClick={item.onSelect}
                      onMouseEnter={() => {
                        if (item.preloadLevelId) {
                          preload(item.preloadLevelId);
                        }
                      }}
                      onFocus={() => {
                        setFocusedIndex(index);
                        if (item.preloadLevelId) {
                          preload(item.preloadLevelId);
                        }
                      }}
                      onKeyDown={(event) => handleKeyDown(event, index)}
                      data-menu-key={item.key}
                      style={{ "--item-enter-index": index } as React.CSSProperties}
                    >
                      <Icon name={item.icon} size={24} decorative className="title-menu__icon" />
                      <span className="title-menu__text">
                        <span className="title-menu__label">{item.label}</span>
                        <span className="title-menu__note">{item.note}</span>
                      </span>
                      {/* 打鍵で進めることを常に示す（ui-design.md §5.1）。 */}
                      {item.primary && (
                        <span className="title-menu__key" aria-hidden="true">
                          ⏎
                        </span>
                      )}
                      <Icon name="next" size={18} decorative className="title-menu__chevron" />
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {isMobileOrNarrow && (
              <p className="title-menu__mobile-note">
                稽古はキーボードでの利用を前提にしています。ここでは話と免状帳を読めます。
              </p>
            )}

            {status === "error" && (
              <p className="title-menu__error" role="alert">
                教材の目次を読み込めませんでした。段をえらぶ から やり直せます。
              </p>
            )}
          </div>

          {/* 師範代ノーマ。門で出迎える側なので、品書きと同じ高さに立たせる。 */}
          <div className="title-menu__stage">
            <p className="title-menu__speech">
              <span className="title-menu__speaker">{LABELS.companion}</span>
              <span className="title-menu__speech-body">{speech}</span>
            </p>
            <img
              className="title-menu__tachie"
              src={companionAssetPath(hero.expression)}
              width={1024}
              height={1536}
              alt=""
              aria-hidden="true"
            />
          </div>
        </div>
      </MainZone>
    </div>
  );
}
